import { useState } from 'react';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Search } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import formatCostCents from '../helpers/formatCostCents';
import getIsActiveSidebarItem from '../helpers/getIsActiveSidebarItem';
import getSidebarIcon from '../helpers/getSidebarIcon';
import filterProviderSidebarItems from './helpers/filterProviderSidebarItems';
import sortProviderSidebarItems from './helpers/sortProviderSidebarItems';
import type {
    CostSidebarItem,
    PlatformCostsSidebarProps,
    ProviderSidebarSortDirection,
} from './types';
import {
    AddCustomProviderButton,
    SearchContainer,
    SearchIcon,
    Sidebar,
    SidebarEmptyState,
    SidebarFooter,
    SidebarItemAmount,
    SidebarItemButton,
    SidebarItemHint,
    SidebarItemLabel,
    SidebarList,
    SidebarScroll,
    SidebarSectionCount,
    SidebarSectionHeader,
    SidebarSectionLabel,
    SidebarSectionLabelGroup,
    SidebarTitle,
    SortButton,
} from './styled';

const BASE_PATH = '/admin/platform-costs';

function PlatformCostsSidebar({
    items,
    month,
    currency,
    onAddCustomProvider,
}: PlatformCostsSidebarProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const [providerQuery, setProviderQuery] = useState('');
    const [sortDirection, setSortDirection] = useState<ProviderSidebarSortDirection>('desc');

    const overviewItem = items.find(item => item.key === 'overview');
    const providerItems = items.filter(item => item.key !== 'overview');
    const filteredProviderItems = filterProviderSidebarItems({
        items: providerItems,
        query: providerQuery,
    });
    const visibleProviderItems = sortProviderSidebarItems({
        items: filteredProviderItems,
        sortDirection,
    });

    const renderItem = (sidebarItem: CostSidebarItem) => (
        <SidebarItemButton
            key={sidebarItem.key}
            type="button"
            $isActive={getIsActiveSidebarItem({
                pathname: location.pathname,
                basePath: BASE_PATH,
                itemPath: sidebarItem.path,
                itemKey: sidebarItem.key,
            })}
            $isEnabled={sidebarItem.isEnabled}
            disabled={!sidebarItem.isEnabled}
            onClick={() => {
                if (!sidebarItem.isEnabled) {
                    return;
                }
                const path = sidebarItem.path ? `${BASE_PATH}/${sidebarItem.path}` : BASE_PATH;
                navigate(`${path}?month=${month}`);
            }}
        >
            {getSidebarIcon({ key: sidebarItem.key, size: 14 })}
            <SidebarItemLabel>{sidebarItem.label}</SidebarItemLabel>
            {sidebarItem.amountCents !== null ? (
                <SidebarItemAmount>
                    {formatCostCents({ amountCents: sidebarItem.amountCents, currency })}
                </SidebarItemAmount>
            ) : null}
            {sidebarItem.amountCents === null && sidebarItem.hint ? (
                <SidebarItemHint>{sidebarItem.hint}</SidebarItemHint>
            ) : null}
        </SidebarItemButton>
    );

    return (
        <Sidebar>
            <SidebarScroll>
                <SidebarTitle>Platform Costs</SidebarTitle>
                <SidebarList>{overviewItem ? renderItem(overviewItem) : null}</SidebarList>
                <SidebarSectionHeader>
                    <SidebarSectionLabelGroup>
                        <SidebarSectionLabel>Providers</SidebarSectionLabel>
                        <SidebarSectionCount>({providerItems.length})</SidebarSectionCount>
                    </SidebarSectionLabelGroup>
                    <SortButton
                        type="button"
                        aria-label={
                            sortDirection === 'desc'
                                ? 'Sort by spend: highest first. Click to reverse.'
                                : 'Sort by spend: lowest first. Click to reverse.'
                        }
                        title={
                            sortDirection === 'desc' ? 'Highest spend first' : 'Lowest spend first'
                        }
                        onClick={() => {
                            setSortDirection(currentDirection =>
                                currentDirection === 'desc' ? 'asc' : 'desc'
                            );
                        }}
                    >
                        {sortDirection === 'desc' ? (
                            <ArrowDownWideNarrow size={14} aria-hidden="true" />
                        ) : (
                            <ArrowUpNarrowWide size={14} aria-hidden="true" />
                        )}
                        Spend
                    </SortButton>
                </SidebarSectionHeader>
                <SearchContainer>
                    <SearchIcon>
                        <Search size={14} aria-hidden="true" />
                    </SearchIcon>
                    <Input
                        type="text"
                        value={providerQuery}
                        placeholder="Search providers..."
                        aria-label="Search providers"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        name="platform-costs-provider-search"
                        data-1p-ignore
                        data-lpignore="true"
                        data-form-type="other"
                        onChange={event => setProviderQuery(event.target.value)}
                    />
                </SearchContainer>
                {visibleProviderItems.length === 0 ? (
                    <SidebarEmptyState>
                        {providerQuery.trim().length > 0
                            ? 'No providers match your search.'
                            : 'No providers yet.'}
                    </SidebarEmptyState>
                ) : (
                    <SidebarList>{visibleProviderItems.map(renderItem)}</SidebarList>
                )}
            </SidebarScroll>
            <SidebarFooter>
                <AddCustomProviderButton type="button" onClick={onAddCustomProvider}>
                    + Add custom provider
                </AddCustomProviderButton>
            </SidebarFooter>
        </Sidebar>
    );
}

export default PlatformCostsSidebar;
